/**
 * A CASCA Marçal Sistema (`?ui=sistema`).
 *
 * [Bruno 2026-10-02] "Cara de sistema, menos cara de IA": menu lateral navy com
 * os 9 módulos, barra do topo com o caminho da tela e a busca ⌘K, e a área de
 * trabalho em fundo neutro. Estilo em styles/sistema.css (classes `ck-`).
 *
 * Fica SEPARADA do LayoutEscritorio de propósito: aquele é a navegação de
 * produção da equipe inteira, e aqui o pior caso é a casca em prévia sair
 * torta para quem ligou o interruptor. As telas de dentro (Outlet) são as
 * mesmas; elas migram para o visual novo uma a uma.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { NavLink, Link, Outlet, useLocation } from 'react-router-dom';
import api from '../services/api';
import AlertasPopup from './AlertasPopup';
import AvisoWhatsapp from './AvisoWhatsapp';
import LembreteFinanceiro from './LembreteFinanceiro';
import { menuDoPapel, localizarRota, casaItem, consultaCasa, MENU_CONFIG, abasDaTela } from '../lib/menuSistema';
import CommandPalette from './CommandPalette';
import TrocaSenhaModal from './TrocaSenhaModal';
import ConfigurarMfaModal from './ConfigurarMfaModal';
import PwaInstallBanner from './PwaInstallBanner';
import AvisoVersaoNova from './AvisoVersaoNova';
import { definirTema, temaAtivo } from '../lib/temaPreview';
import ChatEquipeNotificador from './ChatEquipeNotificador';
import { usePollingVisivel } from '../lib/pollingVisivel';

/* Ícones de traço (família Lucide), desenhados a 16px. Sem emoji. */
const DESENHOS = {
  home: <><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><path d="M9 22V12h6v10" /></>,
  users: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
  calc: <><rect width="16" height="20" x="4" y="2" rx="2" /><path d="M8 6h8" /><path d="M16 14v4" /><path d="M16 10h.01M12 10h.01M8 10h.01M12 14h.01M8 14h.01M12 18h.01M8 18h.01" /></>,
  receipt: <><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z" /><path d="M8 8h8M8 12h8M8 16h5" /></>,
  book: <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" />,
  folder: <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />,
  msg: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
  calendar: <><rect width="18" height="18" x="3" y="4" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></>,
  headset: <><path d="M3 14h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a9 9 0 0 1 18 0v7a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3" /></>,
  chart: <><path d="M3 3v18h18" /><path d="M18 17V9M13 17V5M8 17v-3" /></>,
  shield: <><path d="M12 3l8 3v6c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V6l8-3z" /><path d="M9 12l2 2 4-4" /></>,
  briefcase: <><rect width="20" height="14" x="2" y="7" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></>,
  phone: <><rect width="14" height="20" x="5" y="2" rx="2" /><path d="M12 18h.01" /></>,
  leaf: <><path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" /><path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" /></>,
  server: <><rect width="20" height="8" x="2" y="2" rx="2" /><rect width="20" height="8" x="2" y="14" rx="2" /><path d="M6 6h.01M6 18h.01" /></>,
  search: <><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></>,
  user: <><circle cx="12" cy="8" r="4" /><path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" /></>,
  box: <><path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z" /><path d="M12 22V12" /><path d="m3.3 7 8.7 5 8.7-5" /></>,
  apps: <><rect width="7" height="7" x="3" y="3" rx="1.5" /><rect width="7" height="7" x="14" y="3" rx="1.5" /><rect width="7" height="7" x="3" y="14" rx="1.5" /><rect width="7" height="7" x="14" y="14" rx="1.5" /></>,
  wallet: <><path d="M19 7V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3" /><path d="M21 11h-5a2 2 0 0 0 0 4h5v-4Z" /></>,
  flask: <><path d="M9 3h6M10 3v6L4.5 18.5A2 2 0 0 0 6.2 21.5h11.6a2 2 0 0 0 1.7-3L14 9V3" /><path d="M7 15h10" /></>,
  bot: <><rect width="16" height="12" x="4" y="8" rx="3" /><path d="M12 4v4M9 13h.01M15 13h.01M9 17h6" /></>,
  star: <path d="m12 2 3.1 6.3 6.9 1-5 4.9 1.2 6.8L12 17.8 5.8 21l1.2-6.8-5-4.9 6.9-1z" />,
  gear: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" /></>,
  down: <path d="m6 9 6 6 6-6" />,
  right: <path d="m9 18 6-6-6-6" />,
  menu: <><path d="M4 6h16" /><path d="M4 12h16" /><path d="M4 18h16" /></>,
  close: <><path d="M18 6 6 18" /><path d="m6 6 12 12" /></>,
};

export function Ic({ nome, className = 'ic' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      {DESENHOS[nome] || DESENHOS.server}
    </svg>
  );
}

function usuarioLocal() {
  try { return JSON.parse(localStorage.getItem('usuario') || 'null'); } catch (_) { return null; }
}

function iniciais(nome) {
  if (!nome) return '··';
  const p = String(nome).trim().split(/\s+/);
  return ((p[0]?.[0] || '') + (p.length > 1 ? p[p.length - 1][0] : '')).toUpperCase() || '··';
}

function abrirBusca() {
  document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true, bubbles: true }));
}

export default function LayoutSistema() {
  const location = useLocation();
  const usuario = usuarioLocal();
  // Telas da área de testes liberadas pra esta pessoa (a manutenção libera na tela Manutenção).
  const [testesLiberados, setTestesLiberados] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ck_testes_liberados') || '[]'); } catch (_) { return []; }
  });
  useEffect(() => {
    let vivo = true;
    api.get('/area-testes/minhas').then((r) => {
      const rotas = Array.isArray(r.data?.rotas) ? r.data.rotas : [];
      if (!vivo) return;
      setTestesLiberados(rotas);
      try { localStorage.setItem('ck_testes_liberados', JSON.stringify(rotas)); } catch (_) { /* sem armazenamento */ }
    }).catch(() => { /* menu fica com o que já sabia */ });
    return () => { vivo = false; };
  }, []);
  // White label: aparece pra quem o Pablo liberou no painel (a manutenção sempre vê).
  const [wlLiberado, setWlLiberado] = useState(() => { try { return localStorage.getItem('ck_wl_liberado') === '1'; } catch (_) { return false; } });
  useEffect(() => {
    let vivo = true;
    api.get('/white-label/acesso').then((r) => {
      const pode = !!r.data?.pode;
      if (!vivo) return;
      setWlLiberado(pode);
      try { localStorage.setItem('ck_wl_liberado', pode ? '1' : '0'); } catch (_) { /* sem armazenamento */ }
    }).catch(() => { /* fica com o que já sabia */ });
    return () => { vivo = false; };
  }, []);
  const menu = useMemo(() => menuDoPapel(usuario?.papel, usuario?.email, testesLiberados, wlLiberado), [usuario?.papel, usuario?.email, testesLiberados, wlLiberado]);
  // [Bruno 2026-10-10] O técnico mora na engrenagem do rodapé, só para admin.
  const ehAdmin = usuario?.papel === 'admin';
  const menuTodo = useMemo(() => (ehAdmin && menu.length > 1 ? [...menu, MENU_CONFIG] : menu), [menu, ehAdmin]);
  const onde = useMemo(() => localizarRota(location.pathname, menuTodo, location.search), [location.pathname, location.search, menuTodo]);
  const [aberto, setAberto] = useState(onde.modulo?.chave || null);
  const [configAberta, setConfigAberta] = useState(onde.modulo?.chave === 'config');
  const [badges, setBadges] = useState({});

  // Rota desconhecida não fecha o módulo aberto: menu mudo é pior que menu velho.
  useEffect(() => {
    if (!onde.modulo) return;
    if (onde.modulo.chave === 'config') setConfigAberta(true);
    else setAberto(onde.modulo.chave);
  }, [onde.modulo]);

  // Favoritos: cada pessoa fixa as telas que mais usa (fica no navegador dela).
  const chaveFav = `ck_favoritos_${usuario?.id || 'anon'}`;
  const [favoritos, setFavoritos] = useState(() => {
    try { const v = JSON.parse(localStorage.getItem(chaveFav) || '[]'); return Array.isArray(v) ? v : []; } catch (_) { return []; }
  });
  const alternarFavorito = (to) => setFavoritos((atual) => {
    const novo = atual.includes(to) ? atual.filter((x) => x !== to) : [...atual, to];
    try { localStorage.setItem(chaveFav, JSON.stringify(novo)); } catch (_) { /* sem storage: vale só nesta aba */ }
    return novo;
  });
  const itensFavoritos = useMemo(() => {
    const todos = [];
    menuTodo.forEach((m) => (m.itens || []).forEach((it) => { if (it.to) todos.push(it); }));
    return favoritos.map((to) => todos.find((it) => it.to === to)).filter(Boolean);
  }, [favoritos, menuTodo]);
  const abasTela = abasDaTela(location.pathname, location.search);
  const iPrimeiroModulo = menu.findIndex((m) => m.itens);
  // Celular/tablet: o menu vira gaveta (☰). Escolher uma tela fecha a gaveta.
  const [menuCel, setMenuCel] = useState(false);
  // [Bruno 2026-10-09] Tema claro (padrão) ou escuro, escolhido no botão sol/lua.
  const [temaEscuro, setTemaEscuro] = useState(() => temaAtivo() === 'escuro');
  const alternarTema = () => { const v = !temaEscuro; definirTema(v ? 'escuro' : 'claro'); setTemaEscuro(v); };
  // Computador: o mesmo ☰ oculta/mostra o menu lateral (lembra a escolha).
  const [menuOculto, setMenuOculto] = useState(() => {
    // [Pablo 2026-10-26] A Onésia só tem uma tela: o menu lateral começa oculto
    // pra sobrar espaço (o ☰ mostra; a escolha dela vale dali em diante).
    try {
      const v = localStorage.getItem('ck_menu_oculto');
      if (v === null) return usuarioLocal()?.papel === 'primeiro_contato';
      return v === '1';
    } catch (_) { return false; }
  });
  const ehCelular = () => typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 900px)').matches;
  const alternarMenu = () => {
    if (ehCelular()) { setMenuCel((v) => !v); return; }
    setMenuOculto((v) => {
      const novo = !v;
      try { localStorage.setItem('ck_menu_oculto', novo ? '1' : '0'); } catch (_) { /* sem storage */ }
      return novo;
    });
  };
  useEffect(() => { setMenuCel(false); }, [location.pathname, location.search]);
  useEffect(() => {
    if (!menuCel) return undefined;
    const esc = (e) => { if (e.key === 'Escape') setMenuCel(false); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [menuCel]);

  const carregarBadges = useCallback(() => {
    api.get('/painel/menu-badges')
      .then(({ data }) => setBadges((b) => ({ ...(data || {}), chat: b.chat || 0 })))
      .catch(() => { /* menu sem número é melhor que menu sem menu */ });
  }, []);

  // Não lidas do chat da equipe: quem conta é o aviso do layout (tempo real).
  useEffect(() => {
    const f = (e) => setBadges((b) => ({ ...b, chat: Number(e.detail?.total) || 0 }));
    window.addEventListener('chat-equipe-naolidas', f);
    return () => window.removeEventListener('chat-equipe-naolidas', f);
  }, []);

  // Aba escondida não pergunta; ao voltar, atualiza na hora (lib/pollingVisivel).
  usePollingVisivel(carregarBadges, 60000);

  const n = (chave) => (chave && badges[chave] > 0 ? badges[chave] : null);

  // Um item de tela (dentro de um módulo, dos favoritos ou da engrenagem).
  const linkItem = (it, prefixo = '') => {
    const fixo = favoritos.includes(it.to);
    return (
      <div className="ck-side__linha" key={prefixo + it.to}>
        <NavLink
          to={it.to}
          end={it.to === '/escritorio/notas'}
          className={({ isActive }) => {
            const on = it.to.includes('?')
              ? casaItem(it.to, location.pathname, location.search)
              : (isActive || (it.tambem || []).some((t) => casaItem(t, location.pathname, location.search)))
                && !consultaCasa(location.pathname, location.search, menuTodo);
            return `ck-side__item${on ? ' is-active' : ''}`;
          }}
        >
          <span className="ck-side__rot">{it.label}</span>
          {n(it.badge) && (
            <span className={`ck-count${it.tom === 'critico' ? ' ck-count--alert' : ''}`}>{n(it.badge)}</span>
          )}
        </NavLink>
        <button type="button" className={`ck-side__estrela${fixo ? ' is-fixa' : ''}`}
          aria-pressed={fixo}
          aria-label={fixo ? `Tirar ${it.label} dos favoritos` : `Fixar ${it.label} nos favoritos`}
          title={fixo ? 'Tirar dos favoritos' : 'Fixar nos favoritos'}
          onClick={() => alternarFavorito(it.to)}>
          <Ic nome="star" />
        </button>
      </div>
    );
  };

  // Título da seção só quando ela COMEÇA: dois módulos seguidos da mesma seção
  // (Financeiro e Gestão) não repetem "Administração".
  const grupoAntes = (idx) => {
    for (let i = idx - 1; i >= 0; i--) if (menu[i].grupo) return menu[i].grupo;
    return null;
  };

  return (
    <div className={`ck ck-shell${menuCel ? ' is-menu-cel' : ''}${menuOculto ? ' is-menu-oculto' : ''}`}>
      {menuCel && <div className="ck-scrim" onClick={() => setMenuCel(false)} aria-hidden="true" />}
      <aside className="ck-side" aria-label="Menu">
        <div className="ck-side__brand">
          <img src="/logos/marcal-horizontal-branca.svg" alt="Marçal Contabilidade" />
        </div>

        <nav className="ck-side__nav" aria-label="Módulos">
          {menu.map((m, idx) => {
            const expandido = aberto === m.chave;
            const ehAtual = onde.modulo?.chave === m.chave;
            const cnt = n(m.badge);
            return (
              <React.Fragment key={m.chave}>
                {idx === iPrimeiroModulo && itensFavoritos.length > 0 && (
                  <>
                    <div className="ck-side__group ck-side__group--fav"><Ic nome="star" />Favoritos</div>
                    <div className="ck-side__sub ck-side__sub--fav">{itensFavoritos.map((it) => linkItem(it, 'fav:'))}</div>
                  </>
                )}
                {m.grupo && m.grupo !== grupoAntes(idx) && <div className={`ck-side__group${m.area ? ` ck-side__group--${m.area}` : ''}`}>{m.grupo}</div>}

                {m.to ? (
                  <NavLink to={m.to} className={({ isActive }) => `ck-side__item${isActive ? ' is-active' : ''}`}>
                    <Ic nome={m.icone} />{m.label}
                    {cnt && <span className="ck-count">{cnt}</span>}
                  </NavLink>
                ) : (
                  <button
                    type="button"
                    className={`ck-side__item${m.area ? ` ck-side__item--${m.area}` : ''}${ehAtual && !expandido ? ' is-active' : ''}${expandido ? ' is-open' : ''}`}
                    aria-expanded={expandido}
                    onClick={() => setAberto(expandido ? null : m.chave)}
                  >
                    <Ic nome={m.icone} />{m.label}
                    {cnt && !expandido
                      ? <span className={`ck-count${m.chave === 'atendimento' ? ' ck-count--alert' : ''}`}>{cnt}</span>
                      : <Ic nome={expandido ? 'down' : 'right'} className="ic ck-side__chev" />}
                  </button>
                )}

                {expandido && m.itens && (
                  <div className="ck-side__sub">
                    {m.itens.map((it, i) => (it.sep
                      ? <div className="ck-side__sep" key={`s${i}`}>{it.sep}</div>
                      : linkItem(it)))}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </nav>

        {ehAdmin && configAberta && (
          <div className="ck-side__config" aria-label="Configurações do sistema">
            <div className="ck-side__config-titulo">Configurações do sistema</div>
            <div className="ck-side__sub">
              {MENU_CONFIG.itens.map((it, i) => (it.sep
                ? <div className="ck-side__sep" key={`c${i}`}>{it.sep}</div>
                : linkItem(it, 'cfg:')))}
            </div>
          </div>
        )}

        <div className="ck-side__foot">
          <NavLink to="/escritorio/minha-conta" className="ck-side__item">
            <Ic nome="user" />{usuario?.nome || 'Minha conta'}
          </NavLink>
          {ehAdmin && menu.length > 1 && (
            <button type="button" className={`ck-side__engrenagem${configAberta ? ' is-open' : ''}`}
              aria-expanded={configAberta} aria-label="Configurações do sistema" title="Configurações do sistema"
              onClick={() => setConfigAberta((v) => !v)}>
              <Ic nome="gear" />
            </button>
          )}
        </div>
      </aside>

      <header className="ck-top">
        <button type="button" className="ck-menu-btn"
          aria-label={menuCel ? 'Fechar menu' : (menuOculto ? 'Mostrar menu' : 'Ocultar menu')}
          title={menuOculto ? 'Mostrar menu' : 'Ocultar menu'}
          aria-expanded={menuCel || !menuOculto} onClick={alternarMenu}>
          <Ic nome={menuCel ? 'close' : 'menu'} />
        </button>
        <nav className="ck-crumbs" aria-label="Você está em">
          {onde.modulo && onde.item && <span>{onde.modulo.label}</span>}
          {onde.modulo && onde.item && <span aria-hidden="true">/</span>}
          <span aria-current="page">{onde.item?.label || onde.modulo?.label || 'Cockpit'}</span>
        </nav>
        <div className="ck-top__spacer" />
        {badges.ambiente_teste && (
          <span title="Banco de teste com dados fictícios: nada aqui chega a cliente, Receita ou WhatsApp"
            style={{ background: 'var(--ckd-bg-f5b301, #f5b301)', color: 'var(--ckd-tx-1a1a1a, #1a1a1a)', fontWeight: 700, fontSize: 12, padding: '5px 10px', borderRadius: 6, marginRight: 10, whiteSpace: 'nowrap' }}>
            AMBIENTE DE TESTE
          </span>
        )}
        {usuario?.papel !== 'primeiro_contato' && (
          <button type="button" className="ck-search" onClick={abrirBusca}>
            <Ic nome="search" />Buscar empresa, CNPJ ou tela<kbd>⌘K</kbd>
          </button>
        )}
        <button type="button" className="ck-tema-btn" onClick={alternarTema}
          aria-label={temaEscuro ? 'Usar tema claro' : 'Usar tema escuro'}
          title={temaEscuro ? 'Tema claro' : 'Tema escuro'}>
          {temaEscuro ? (
            <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
              <circle cx="12" cy="12" r="4.2" /><path d="M12 2.5v2.2M12 19.3v2.2M4.6 4.6l1.6 1.6M17.8 17.8l1.6 1.6M2.5 12h2.2M19.3 12h2.2M4.6 19.4l1.6-1.6M17.8 6.2l1.6-1.6" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z" />
            </svg>
          )}
        </button>
        <span className="ck-avatar" title={usuario?.nome || ''}>{iniciais(usuario?.nome)}</span>
      </header>

      <main className="ck-main">
        {abasTela && (
          <nav className="ck-abas-tela" aria-label="Visões desta tela">
            {abasTela.abas.map(([rot, to], i) => (
              <Link key={to} to={to} className={`ck-abas-tela__aba${i === abasTela.ativa ? ' is-active' : ''}`}
                aria-current={i === abasTela.ativa ? 'page' : undefined}>{rot}</Link>
            ))}
          </nav>
        )}
        <Outlet />
      </main>

      <CommandPalette menu={menuTodo} />
      <TrocaSenhaModal />
      <ConfigurarMfaModal />
      <PwaInstallBanner />
      <AvisoVersaoNova />
      <ChatEquipeNotificador />
      <AlertasPopup />
      <AvisoWhatsapp />
      <LembreteFinanceiro />
    </div>
  );
}
